import { defineNuxtPlugin } from '#app';

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();

  const MAX_RELOAD_ATTEMPTS = 3;
  const RELOAD_ATTEMPT_TIMEOUT = 30000;
  const ERROR_COOKIE_TTL = 60000; // 1 минута

  let reloadAttempts = 0;
  let lastAttemptTime = 0;

  // Белый список ошибок Swiper (расширенный)
  const SWIPER_ERROR_WHITELIST = [
    // Windows/Linux/Android ошибки
    'Cannot read properties of undefined (reading \'removeEventListener\')',
    'Cannot read property \'removeEventListener\' of undefined',
    'Cannot read properties of null (reading \'removeEventListener\')',

    // macOS/iOS ошибки
    'undefined is not an object (evaluating',
    'null is not an object (evaluating',

    // при желании можно расширить
    // // Общие ошибки Swiper
    // 'swiper',
    // 'Swiper',
    // 'destroyed',
    // 'el is null',
    // 'el is undefined',
    //
    // // Ошибки событий
    // 'wheel',
    // 'touch',
    // 'resize',
    // 'scroll',
    //
    // // Минимизированные версии ошибок (для production)
    // 'n[a]', // выпадает на macOs, если первая минимальная итерация не пройдет
    // 't[a]',
    // 'e.removeEventListener',
    // 'r.removeEventListener'
  ];

  const ERROR_COOKIE_NAME = 'swiper_error_reload';

  // Установка куки с защитой от циклов
  const setSwiperErrorCookieAndReload = (errorPattern: string) => {
    try {
      // Проверяем, не установлена ли уже кука
      const existingCookie = document.cookie.split('; ').find((c) => c.startsWith(`${ERROR_COOKIE_NAME}=`));
      if (existingCookie) {
        console.log('Cookie уже установлена, пропускаем перезагрузку');
        return;
      }

      // Устанавливаем куку на 1 минуту
      const expires = new Date(Date.now() + ERROR_COOKIE_TTL).toUTCString();
      const cookieValue = `${ERROR_COOKIE_NAME}=${btoa(errorPattern).substring(0, 10)}; expires=${expires}; path=/; SameSite=Lax`;
      document.cookie = cookieValue;

      console.log('Обнаружена ошибка Swiper, перезагружаем страницу...', errorPattern);

      // Задержка перед перезагрузкой для завершения текущих операций
      setTimeout(() => {
        window.location.reload();
      }, 100);

    } catch (err) {
      console.error('Ошибка в обработчике Swiper:', err);
    }
  };

  // Функция проверки ошибки по белому списку
  const isSwiperError = (errorMessage: string): boolean => {
    if (!errorMessage || typeof errorMessage !== 'string') return false;

    const message = errorMessage.toLowerCase();
    return SWIPER_ERROR_WHITELIST.some((pattern) =>
      message.includes(pattern.toLowerCase())
    );
  };

  // Обработчик ошибок Swiper
  const handleSwiperError = (error: any, context: string = 'unknown') => {
    if (!process.client) return;

    const errorMessage = String(error?.message || error || '');

    if (isSwiperError(errorMessage)) {
      console.warn(`Обнаружена ошибка Swiper (${context}):`, errorMessage);

      // Проверяем лимиты перезагрузки (только для chunk ошибок)
      const now = Date.now();
      if (now - lastAttemptTime > RELOAD_ATTEMPT_TIMEOUT) {
        reloadAttempts = 0;
        lastAttemptTime = now;
      }

      if (reloadAttempts < MAX_RELOAD_ATTEMPTS) {
        setSwiperErrorCookieAndReload(errorMessage);
      } else {
        console.error('Достигнут максимум перезагрузок. Очистите кеш браузера.');
      }
    }
  };

  // Обработчик ошибок загрузки чанков
  router.onError((error: any) => {
    const isLoadingChunk = Boolean(String(error?.message || "").includes('Loading chunk'));
    const isFailedLoadResource = Boolean(String(error?.message || "").includes('Failed to load resource'));
    const isFailedDynamicallyImport = Boolean(String(error?.message || "").includes('Failed to fetch dynamically imported module'));

    if (isLoadingChunk || isFailedLoadResource || isFailedDynamicallyImport) {
      const now = Date.now();
      if (now - lastAttemptTime > RELOAD_ATTEMPT_TIMEOUT) {
        reloadAttempts = 0;
        lastAttemptTime = now;
      }
      if (reloadAttempts < MAX_RELOAD_ATTEMPTS) {
        reloadAttempts++;
        setTimeout(() => {
          window.location.href = window.location.href + (window.location.href.includes('?') ? '&' : '?') + '_t=' + now;
        }, 1000);
      } else {
        console.error('Max reload attempts reached. Please try clearing your browser cache or contact support.');
      }
    }

    // Дополнительно проверяем на ошибки Swiper в ошибках маршрутизатора
    handleSwiperError(error, 'router');
  });

  // Глобальные обработчики ошибок (только на клиенте)
  if (process.client) {
    // Обработчик глобальных ошибок
    window.addEventListener('error', (event: ErrorEvent) => {
      handleSwiperError(event.error || event.message, 'global_error');
    });

    // Обработчик необработанных Promise rejection
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      handleSwiperError(event.reason, 'unhandled_rejection');
    });

    // Обработчик ошибок Vue
    nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
      handleSwiperError(error, `vue_${info}`);
    };

    // Очистка куки при успешной загрузке
    router.afterEach(() => {
      setTimeout(() => {
        if (document.cookie.includes(ERROR_COOKIE_NAME)) {
          document.cookie = `${ERROR_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          console.log('Cookie ошибки Swiper очищена');
        }
      }, 2000);
    });

    // Дополнительная защита: очистка куки при обновлении страницы
    window.addEventListener('beforeunload', () => {
      if (document.cookie.includes(ERROR_COOKIE_NAME)) {
        document.cookie = `${ERROR_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });

    // Инициализация: очистка старых кук при загрузке
    if (document.cookie.includes(ERROR_COOKIE_NAME)) {
      setTimeout(() => {
        document.cookie = `${ERROR_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }, 1000);
    }
  }
});
